import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';

import { MatrixUpdatingCard } from '@/components/atoms/MatrixUpdatingCard';
import { RegulatoryTrainingFilterSection } from '@/components/molecules/RegulatoryTrainingFilterSection';
import { RegulatoryTrainingHeaderSection } from '@/components/molecules/RegulatoryTrainingHeaderSection';
import { RegulatoryTrainingIndicators } from '@/components/molecules/RegulatoryTrainingIndicators';
import PositionTrainingMatrix from '@/components/organisms/PositionTraningMatrix'; // TODO: should be renamed to RegulatoryTrainingMatrix
import { TrainingTypeEnum } from '@/constants/trainingTypeOptions';
import { useIndicators } from '@/hooks/useIndicators';
import { useMappingState } from '@/hooks/useMappingState';
import { usePositions } from '@/hooks/usePositions';
import { useTrainingMatrix } from '@/hooks/useTrainingMatrix';
import { useTrainingOperations } from '@/hooks/useTrainingOperations';
import {
	positionTrainingMatrixFiltersAtom, // TODO: should be renamed to regulatoryTrainingMatrixFiltersAtom
} from '@/state/PositionTrainingMatrixFilter.atom';
import TrainingModal from '../../components/organisms/TrainingModal';
import {
	userCanBlockTraining,
	userCanMapRegulatoryTraining,
} from '../../utils/handleSavePermissions';

const RegulatoryTrainingPage = () => {
	const [isMatrixChanging, setIsMatrixChanging] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const filterValues = useRecoilValue(positionTrainingMatrixFiltersAtom);
	const [filteredData, setFilteredData] = useState(filterValues);
	const [matrixRefetch, setMatrixRefetch] = useState(false);
	const [isNewTrainingModalOpen, setIsNewTraininigModalOpen] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [isPositionFilteringIndicator, setIsPositionFilteringIndicator] =
		useState(false);
	const [isTrainingFilteringIndicator, setIsTrainingFilteringIndicator] =
		useState(false);

	const { indicators, fetchIndicators } = useIndicators();
	const {
		modifiedCheckboxes,
		pendingChanges,
		handleMappingChange,
		setModifiedCheckboxes,
		setPendingChanges,
	} = useMappingState();
	const { fetchPositionsByType } = usePositions(filteredData);
	const {
		trainingsData,
		isLoading,
		isError,
		fetchNextPage,
		isFetchingNextPage,
		hasNextPage,
	} = useTrainingMatrix({
		filteredData,
		matrixRefetch,
		isPositionFilteringIndicator,
		isTrainingFilteringIndicator,
		setIsMatrixChanging,
	});
	const { handleSendCombinedChanges } = useTrainingOperations({
		pendingChanges,
		modifiedCheckboxes,
		setModifiedCheckboxes,
		setPendingChanges,
		setIsMatrixChanging,
	});

	const toggleEditMode = useCallback(async () => {
		if (isEditMode) {
			const hasMatrixChanges = Object.keys(pendingChanges.matrix).length > 0;
			const hasBlockMatrixChanges =
				Object.keys(pendingChanges.blockMatrix).length > 0;

			if (hasMatrixChanges || hasBlockMatrixChanges) {
				await handleSendCombinedChanges();
				await fetchIndicators(filterValues);
				await handleMatrixRefetch();
			}
		}
		setIsEditMode((prev) => !prev);
	}, [
		handleSendCombinedChanges,
		isEditMode,
		pendingChanges.blockMatrix,
		pendingChanges.matrix,
	]);

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const handleMatrixRefetch = async () => {
		setMatrixRefetch((prevState) => !prevState);
	};

	const getEffectiveMappedPositions = async (trainingId: number) => {
		const allPositionIds = await fetchPositionsByType('all', trainingId);
		const backendMappedIds = new Set(
			await fetchPositionsByType('mapped', trainingId),
		);
		return allPositionIds.filter((positionId) => {
			const key = `${positionId}-${trainingId}`;
			if (modifiedCheckboxes.matrix.has(key)) {
				return modifiedCheckboxes.matrix.get(key);
			}
			return backendMappedIds.has(positionId);
		});
	};

	const getEffectiveBlockedPositions = async (trainingId: number) => {
		const allPositionIds = await fetchPositionsByType('all', trainingId);
		const backendBlockedIds = new Set(
			await fetchPositionsByType('blocked', trainingId),
		);
		return allPositionIds.filter((positionId) => {
			const key = `${positionId}-${trainingId}`;
			if (modifiedCheckboxes.blockMatrix.has(key)) {
				return modifiedCheckboxes.blockMatrix.get(key);
			}
			return backendBlockedIds.has(positionId);
		});
	};

	const handleMapAllPositions = async (trainingId: number) => {
		setIsMatrixChanging(true);
		try {
			const allPositionIds = await fetchPositionsByType('all', trainingId);
			const mappedPositionIds = await getEffectiveMappedPositions(trainingId);
			const blockedPositionIds = await getEffectiveBlockedPositions(trainingId);
			const unblockedPositionIds = allPositionIds.filter(
				(positionId) => !blockedPositionIds.includes(positionId),
			);
			const allMapped =
				unblockedPositionIds.length > 0 &&
				unblockedPositionIds.every((positionId) =>
					mappedPositionIds.includes(positionId),
				);
			if (allMapped) {
				unblockedPositionIds.forEach((positionId) => {
					handleMappingChange(trainingId, positionId, false);
				});
			} else {
				allPositionIds.forEach((positionId) => {
					handleMappingChange(trainingId, positionId, true);
				});
			}
		} catch (error) {
			toast.error('Erro ao mapear todas as funções', {
				theme: 'colored',
				toastId: `error-${'position-training-link/matrix'}`,
			});
		} finally {
			setIsMatrixChanging(false);
		}
	};

	const handleBlockAllPositions = async (trainingId: number) => {
		setIsMatrixChanging(true);
		try {
			const mappedPositionIds = await getEffectiveMappedPositions(trainingId);
			const blockedPositionIds = await getEffectiveBlockedPositions(trainingId);
			const allBlocked =
				mappedPositionIds.length > 0 &&
				mappedPositionIds.every((positionId) =>
					blockedPositionIds.includes(positionId),
				);
			if (allBlocked) {
				mappedPositionIds.forEach((positionId) => {
					handleMappingChange(trainingId, positionId, false, true);
				});
			} else {
				mappedPositionIds.forEach((positionId) => {
					handleMappingChange(trainingId, positionId, true, true);
				});
			}
		} catch (error) {
			toast.error('Erro ao Bloquear Funções Mapeadas', {
				theme: 'colored',
				toastId: `error-${'position-training-link/block-matrix'}`,
			});
		} finally {
			setIsMatrixChanging(false);
		}
	};

	const canBlockTraining = userCanBlockTraining();
	const canMapRegulatoryTraining = userCanMapRegulatoryTraining();
	const canSave = canMapRegulatoryTraining || canBlockTraining;

	return (
		<div
			className="relative flex w-full flex-col items-start"
			style={{ height: 'calc(100% - 40px)' }}
		>
			<RegulatoryTrainingHeaderSection
				canSave={canSave}
				isEditMode={isEditMode}
				trainingsData={trainingsData}
				filteredData={filteredData}
				toggleEditMode={toggleEditMode}
				setIsNewTraininigModalOpen={setIsNewTraininigModalOpen}
			/>
			{isMatrixChanging && <MatrixUpdatingCard />}
			<RegulatoryTrainingFilterSection
				setFilteredData={setFilteredData}
				fetchIndicators={fetchIndicators}
			/>
			<RegulatoryTrainingIndicators
				indicators={indicators}
				isPositionFilteringIndicator={isPositionFilteringIndicator}
				isTrainingFilteringIndicator={isTrainingFilteringIndicator}
				setIsPositionFilteringIndicator={setIsPositionFilteringIndicator}
				setIsTrainingFilteringIndicator={setIsTrainingFilteringIndicator}
				handleMatrixRefetch={handleMatrixRefetch}
			/>
			<PositionTrainingMatrix
				trainingsMatrixData={trainingsData}
				isLoading={isLoading}
				isError={isError}
				hasNextPage={hasNextPage}
				fetchNextPage={fetchNextPage}
				isFetchingNextPage={isFetchingNextPage}
				filteredData={filteredData}
				needsToRefetch={matrixRefetch}
				setNeedsToRefetch={setMatrixRefetch}
				handleMatrixRefetch={handleMatrixRefetch}
				isModalOpen={isNewTrainingModalOpen}
				setIsModalOpen={setIsNewTraininigModalOpen}
				canSave={canSave}
				isEditMode={isEditMode}
				modifiedCheckboxes={modifiedCheckboxes}
				onMappingChange={handleMappingChange}
				handleMapAllPositions={handleMapAllPositions}
				handleBlockAllPositions={handleBlockAllPositions}
				canBlockTraining={canBlockTraining}
				canMapRegulatoryTraining={canMapRegulatoryTraining}
			/>
			<TrainingModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				matrixRefetch={handleMatrixRefetch}
				trainingType={TrainingTypeEnum.REGULATORY}
			/>
		</div>
	);
};

export default RegulatoryTrainingPage;
